import { ref, getCurrentInstance, computed } from 'vue';
import { useModuleData } from '@/rebuild/hooks/data/useModuleData';
import { useStore } from '@/utils/vue-api';
import { convertNumberToCNUnit } from '@/utils/api';
import dayjs from 'dayjs';
import i18n, { EN } from '@/locales';

/**
 * 多市场股票行情数据管理 Hook
 * 用于管理多个市场的股票行情和K线数据
 * 
 * @returns {Object} 数据管理接口
 */
export function useMultiMarketQuote(options = {}) {
	const vm = getCurrentInstance();

	const store = useStore();
	const marketSource = options.marketSource || store.getters.getMarketSource;
	const getMarketList = Array.isArray(marketSource) ? marketSource : Object.values(marketSource || {});

	const activeFullCode = ref('');
	const fullCodeOptions = computed(() => {

		return getMarketList.map(item => ({
			// todo 文案先写死
			label: item.market.toUpperCase() === 'HK' ? '港股' : 'A股',
			value: item.fullCode,
			market: item.market.toUpperCase()
		}))
	});

	const rycCodeOptions = computed(() => {
		return getMarketList.map(item => ({
			label: item.market.toUpperCase() === 'HK' ? 'H' : 'A',
			value: item.fullCode,
			market: item.market.toUpperCase()
		}))
	});


	const { quote, quote_quoteKLine } = useModuleData();
	const quotesRawData = ref({});

	// 股票行情
	const fetchStockQuotes = async () => {
		const markets = getMarketList;
		const results = await Promise.all(
			markets.map(item =>
				quote.fetch({
					market: item.market,
					code: item.code
				})
			)
		);

		results.forEach(([err, data], index) => {
			const market = markets[index];
			const fullCode = market.fullCode;

			if (!quotesRawData.value[fullCode]) {
				vm.proxy.$set(quotesRawData.value, fullCode, {
					quoteVO: {
						data: null,
						error: null
					},
					klineVO: {
						data: null,
						error: null
					}
				});
			}
			vm.proxy.$set(quotesRawData.value[fullCode], 'quoteVO', {
				data: err ? null : data?.data,
				error: err
			});
		});
	};

	// 股票K线
	const fetchStockKLine = async (params = {}) => {
		const markets = getMarketList;
		const results = await Promise.all(
			markets.map(item =>
				quote_quoteKLine.fetch({
					fullCode: item.fullCode,
					defaultTime: 1,
					...params
				})
			)
		);

		results.forEach(([err, data], index) => {
			const market = markets[index];
			const fullCode = market.fullCode;

			if (!quotesRawData.value[fullCode]) {
				vm.proxy.$set(quotesRawData.value, fullCode, {
					quoteVO: {
						data: null,
						error: null
					},
					klineVO: {
						data: null,
						error: null
					}
				});
			}

			// TODO 港股数据模拟
			// const isHKStock = market.market.toUpperCase() === 'HK';
			// let copyRawData = [...(data?.data || [])];
			// if (isHKStock) {
			// 	copyRawData = copyRawData.map((item, index) => {
			// 		return {
			// 			...item,
			// 			closePrice: index === 0 ? item.closePrice : 0
			// 		};
			// 	});
			// }

			vm.proxy.$set(quotesRawData.value[fullCode], 'klineVO', {
				data: err ? null : data?.data,
				error: err
			});
		});
	};

	// 加载单个市场的行情数据
	const fetchSingleMarketQuote = async fullCode => {
		const market = getMarketList.find(m => m.fullCode === fullCode);
		if (!market) return;

		const [err, data] = await quote.fetch({
			market: market.market,
			code: market.code
		});

		if (!quotesRawData.value[fullCode]) {
			vm.proxy.$set(quotesRawData.value, fullCode, {
				quoteVO: {
					data: null,
					error: null
				},
				klineVO: {
					data: null,
					error: null
				}
			});
		}

		vm.proxy.$set(quotesRawData.value[fullCode], 'quoteVO', {
			data: err ? null : data?.data,
			error: err
		});
	};

	// 加载单个市场的K线数据
	const fetchSingleMarketKLine = async (fullCode, params = {}) => {
		const market = getMarketList.find(m => m.fullCode === fullCode);
		if (!market) return;

		const { defaultTime = 1 } = params;
		const [err, data] = await quote_quoteKLine.fetch({
			fullCode: market.fullCode,
			defaultTime,
			...params
		});

		if (!quotesRawData.value[fullCode]) {
			vm.proxy.$set(quotesRawData.value, fullCode, {
				quoteVO: {
					data: null,
					error: null
				},
				klineVO: {
					data: null,
					error: null
				}
			});
		}

		vm.proxy.$set(quotesRawData.value[fullCode], 'klineVO', {
			data: err ? null : data?.data,
			error: err
		});
	};

	// 加载单个市场的完整数据（行情 + K线）
	const fetchSingleMarketData = async (fullCode, params = {}) => {
		await Promise.all([fetchSingleMarketQuote(fullCode), fetchSingleMarketKLine(fullCode, params)]);
	};

	// 快速访问方法
	const getQuoteVO = fullCode => {
		return quotesRawData.value[fullCode]?.quoteVO || { data: null, error: null };
	};
	const getKlineVO = fullCode => {
		return quotesRawData.value[fullCode]?.klineVO || { data: null, error: null };
	};
	const getMarketData = fullCode => {
		return quotesRawData.value[fullCode] || null;
	};

	const emptyQuoteVO = computed(() => {
		if (!getMarketList.length) return true;
		const quoteVO = getQuoteVO(getMarketList[0].fullCode);
		return !!quoteVO.error;
	});

	// ----------------------------- Tools -----------------------------

	const transformFullCode = item => {
		const { market, code } = item;
		return `${code}.${market.toUpperCase()}`;
	};

	const checkHKStock = marketRow => {
		return marketRow.market.toUpperCase() === 'HK';
	};

	const getRawValueWithQuoteVO = (marketRow, prop) => {
		const quoteRawData = getQuoteVO(marketRow.fullCode).data;
		return quoteRawData?.[prop];
	};

	const cleanRunValueCovert = (val, options) => {
		const { value, unit } = convertNumberToCNUnit(val, options);
		return {
			next(tmpl = '') {
				if (tmpl) {
					return tmpl.replace(/{template}/g, `${value}${unit}`);
				}
				return `${value}${unit}`;
			}
		};
	};
	const cleanFormatQuoteRawDataFn = (data, prop) => {
		const rawValue = data?.[prop];
		if (typeof rawValue === 'undefined') return '';

		switch (prop) {
			case 'closePrice':
				return rawValue;

			case 'change':
				return rawValue > 0 ? `+${rawValue?.toFixed(2)}` : `${rawValue?.toFixed(2)}`;
			case 'changePct':
				return rawValue > 0 ? `+${rawValue?.toFixed(2)}%` : `${rawValue?.toFixed(2)}%`;

			case 'highPrice':
			case 'lowPrice':
				return cleanRunValueCovert(rawValue, { unitType: 'en' }).next();

			case 'turnoverVolume':
				return cleanRunValueCovert(rawValue * 100, { unitType: 'en' }).next();
			case 'turnoverVolumeShares':
				return cleanRunValueCovert(rawValue, { unitType: 'en' }).next();

			case 'turnoverValue':
				return cleanRunValueCovert(rawValue, { unitType: 'en' }).next();

			case 'totalMv':
				return cleanRunValueCovert(rawValue, { unitType: 'en' }).next();

			default:
				return rawValue;
		}
	};

	const runValueCovert = val => {
		const { value, unit } = convertNumberToCNUnit(val);
		return {
			next(tmpl = '') {
				if (tmpl) {
					return tmpl.replace(/{template}/g, `${value}${unit}`);
				}
				return `${value}${unit}`;
			}
		};
	};
	const formatQuoteRawDataFn = (marketRow, prop) => {
		const isHKStock = checkHKStock(marketRow);
		const rawValue = getRawValueWithQuoteVO(marketRow, prop);
		if (typeof rawValue === 'undefined') return '';

		switch (prop) {
			case 'closePrice':
				return rawValue;

			case 'change':
				return rawValue > 0 ? `+${rawValue?.toFixed(2)}` : `${rawValue?.toFixed(2)}`;
			case 'changePct':
				return rawValue > 0 ? `+${rawValue?.toFixed(2)}%` : `${rawValue?.toFixed(2)}%`;

			case 'highPrice':
			case 'lowPrice':
				return runValueCovert(rawValue).next();

			case 'turnoverVolume':
				if (i18n.locale === EN) {
					// 英文，将 turnoverVolume 单位由手转换为股
					return runValueCovert(rawValue * 100).next();
				}
				return runValueCovert(rawValue).next('{template}手');
			case 'turnoverVolumeShares':
				if (i18n.locale === EN) {
					return runValueCovert(rawValue).next();
				}
				return runValueCovert(rawValue).next('{template}股');

			case 'turnoverValue':
				return runValueCovert(rawValue).next();

			case 'tradingDay':
				return formatTradingDayFn(rawValue, isHKStock);

			default:
				return rawValue;
		}
	};
	const formatTradingDayFn = (date, isHKStock) => {
		const formatTime = dayjs(date).valueOf();
		const formatValue = dayjs(formatTime).format('YYYY-MM-DD');
		return `${vm.proxy.$t('enterprise.common-a.until')} ${formatValue} ${isHKStock ? '16:00' : '15:00'
			} （${vm.proxy.$t('enterprise.UTC')}）`;
	};
	const formatTradingDayInMobile = (date, isHKStock) => {
		return `${date} ${isHKStock ? '16:00' : '15:00'}`;
	};

	return {
		fetchStockQuotes,
		fetchStockKLine,
		fetchSingleMarketQuote,
		fetchSingleMarketKLine,
		fetchSingleMarketData,
		quotesRawData,

		getQuoteVO,
		getKlineVO,
		getMarketData,
		getMarketList,
		emptyQuoteVO,

		transformFullCode,
		getRawValueWithQuoteVO,
		formatQuoteRawDataFn,
		formatTradingDayFn,
		formatTradingDayInMobile,

		activeFullCode,
		fullCodeOptions,
		rycCodeOptions,
		cleanFormatQuoteRawDataFn
	};
}

